import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Login() {
  const { loginEmail } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [params] = useSearchParams();
  const isAdminFlow = params.get("from") === "admin";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await loginEmail(email, password);
      toast.success(t("auth.success"));
      if (u.is_admin) {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Identifiants invalides.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex">
      <div className="hidden lg:flex flex-1 relative border-r border-white/10">
        <img
          src="https://images.unsplash.com/photo-1576913105965-1d0b6a19a482?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBtYW4lMjBwb3J0cmFpdCUyMGRhcmt8ZW58MHx8fHwxNzc3NjU1OTMxfDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover grayscale opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950/40 via-zinc-950/70 to-zinc-950" />
        <div className="relative p-12 flex flex-col justify-between w-full">
          <Link to="/" className="flex items-center gap-2">
            <span className="block w-2.5 h-2.5 bg-white" />
            <span className="font-display text-xl">Versant<span className="text-zinc-500">.</span>Studio</span>
          </Link>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-zinc-400 mb-3">
              {isAdminFlow ? t("admin.title") : t("dash.section").replace("· ", "")}
            </p>
            <h2 className="font-display text-4xl xl:text-5xl tracking-tighter max-w-md italic text-zinc-300">
              Bon retour.
            </h2>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-10">
            <span className="block w-2.5 h-2.5 bg-white" />
            <span className="font-display text-xl">Versant<span className="text-zinc-500">.</span>Studio</span>
          </Link>
          <p className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">{t("auth.login.label")}</p>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tighter mb-8">{t("auth.login.title")}</h1>

          {!isAdminFlow && (
            <>
              <button
                type="button"
                onClick={startGoogleLogin}
                data-testid="login-google-btn"
                className="w-full flex items-center justify-center gap-3 bg-white text-zinc-950 hover:bg-zinc-200 transition-colors px-6 py-3.5 text-sm font-medium mb-5"
              >
                <GoogleIcon />
                {t("common.continue_with_google")}
              </button>

              <div className="flex items-center gap-3 my-6">
                <span className="flex-1 h-px bg-white/10" />
                <span className="text-xs uppercase tracking-[0.2em] text-zinc-500">{t("common.or")}</span>
                <span className="flex-1 h-px bg-white/10" />
              </div>
            </>
          )}

          <form onSubmit={submit} data-testid="login-form" className="space-y-5">
            <Field label={t("auth.email")}>
              <input
                required
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-zinc-900 border border-white/10 px-4 py-3 text-sm focus:border-white focus:outline-none focus:ring-1 focus:ring-white"
              />
            </Field>
            <Field label={t("auth.password")}>
              <input
                required
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-zinc-900 border border-white/10 px-4 py-3 text-sm focus:border-white focus:outline-none focus:ring-1 focus:ring-white"
              />
            </Field>
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-zinc-100 text-zinc-950 hover:bg-white transition-colors px-6 py-3.5 text-sm font-medium disabled:opacity-50"
            >
              {loading ? t("auth.login.busy") : t("auth.login.cta")}
              {!loading && <ArrowRight size={14} />}
            </button>
          </form>

          {!isAdminFlow && (
            <p className="text-sm text-zinc-400 mt-8">
              {t("auth.login.no_account")}{" "}
              <Link to="/register" data-testid="login-register-link" className="text-white underline underline-offset-4">
                {t("auth.login.create")}
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 mb-2">{label}</span>
    {children}
  </label>
);

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.7 1.22 9.2 3.6l6.85-6.85C35.9 2.4 30.45 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);
